/**
 * ConstructionDataDisplay Component
 * 
 * Main component for displaying and managing construction worker payslip data.
 * Provides functionality for:
 * - Viewing construction worker data in tables
 * - Filtering data by year, month (給与) / period (賞与), site (現場/genba), and employee name
 * - Switching between 給与 and 賞与 (construction_bonus_data only for 賞与)
 * - Deleting data (by month/period, year, selection, or individual records)

 * - Loading and error state management
 * 
 * Features:
 * - Dynamic filtering with real-time data updates
 * - Multiple delete operations (month, year, site, employee, individual, all)
 * - Loading and error indicators
 * - Delete operation feedback messages
 * - Site (現場) and employee name filters (different from office employee filters)
 * 
 * Note: 現場賞与 uses /api/construction-bonus and construction_bonus_data.
 * It is not 本社 bonus_data and not construction_salary_data.
 * 
 * @component
 */

import React, { useState, useCallback } from 'react';
import '../../css/DataDisplay.css';
import { ConstructionDataTable } from './ConstructionDataTable';
import { DeleteMessages } from './DeleteMessages';
import { LoadingAndError } from './LoadingAndError';
import { DataTypeToggle } from './DataTypeToggle';
import { useConstructionDataFetching } from './hooks/useConstructionDataFetching';
import { useConstructionFilterOptions } from './hooks/useConstructionFilterOptions';
import { useConstructionDeleteOperations } from './hooks/useConstructionDeleteOperations';

/**
 * ConstructionDataDisplay Component
 * 
 * Main display component for construction worker payslip data.
 * Manages filtering, data fetching, and delete operations.
 * 
 * @returns React functional component
 */
export const ConstructionDataDisplay = () => {
  /**
   * State management:
   * - filterYear: Selected year filter (empty string = all years)
   * - filterMonth: Selected month filter (empty string = all months)
   * - filterGenba: Selected site/現場 filter (empty string = all sites)
   * - filterEmployeeName: Selected employee name filter (empty string = all employees)
   */
  const [dataType, setDataType] = useState<'salary' | 'bonus'>('salary');
  const [filterMonth, setFilterMonth] = useState<string>('');
  const [filterYear, setFilterYear] = useState<string>('');
  const [filterPeriod, setFilterPeriod] = useState<string>('');
  const [filterGenba, setFilterGenba] = useState<string>('');
  const [filterEmployeeName, setFilterEmployeeName] = useState<string>('');

  /**
   * Custom hook for construction data fetching.
   * 
   * Provides:
   * - data: Array of construction worker records
   * - loading: Loading state indicator
   * - error: Error message if fetch fails
   * - fetchFilteredData: Function to fetch filtered data
   */
  const { data, loading, error, fetchFilteredData } = useConstructionDataFetching(dataType);
  const {
    availableYears,
    availableMonths,
    availablePeriods,
    availableGenbas,
    availableEmployees,
    loadFilterOptions,
  } = useConstructionFilterOptions(dataType);

  const resetToFullListAfterDelete = useCallback(() => {
    setFilterYear('');
    setFilterMonth('');
    setFilterPeriod('');
    setFilterGenba('');
    setFilterEmployeeName('');
    fetchFilteredData();
    loadFilterOptions();
  }, [fetchFilteredData, loadFilterOptions]);

  /**
   * Custom hook for construction delete operations.
   * 
   * Provides:
   * - deleteLoading: Loading state for delete operations
   * - deleteError: Error message if delete fails
   * - deleteSuccess: Success message after delete
   * - handleDeleteMonthData: Delete all data for a month
   * - handleDeleteYearData: Delete all data for a year
   * - handleDeleteGenbaData: Delete all data for a site (現場)
   * - handleDeleteEmployeeData: Delete all data for an employee
   * - handleDeleteIndividualRecord: Delete a single record
   * - handleDeleteAllData: Delete all construction worker data
   * - clearDeleteMessages: Clear delete operation messages
   * 
   * Callback refetches data after successful delete.
   */
  const {
    deleteLoading,
    deleteError,
    deleteSuccess,
    handleDeleteYearData,
    handleDeleteMonthData,
    handleDeletePeriodData,
    handleDeleteSelection,
    handleDeleteIndividualRecord,
    clearDeleteMessages
  } = useConstructionDeleteOperations(resetToFullListAfterDelete, dataType);

  const monthOrPeriod = dataType === 'bonus' ? filterPeriod : filterMonth;

  const handleDataTypeToggle = () => {
    setDataType(dataType === 'salary' ? 'bonus' : 'salary');
    setFilterYear('');
    setFilterMonth('');
    setFilterPeriod('');
    setFilterGenba('');
    setFilterEmployeeName('');
  };

  /**
   * Handles year filter change.
   * 
   * Updates year filter and immediately fetches filtered data.
   * 
   * @param newYear - Selected year string
   */
  const handleYearChange = (newYear: string) => {
    setFilterYear(newYear);
    fetchFilteredData(newYear, monthOrPeriod, filterGenba, filterEmployeeName);
  };

  const handleMonthChange = (newMonth: string) => {
    setFilterMonth(newMonth);
    fetchFilteredData(filterYear, newMonth, filterGenba, filterEmployeeName);
  };

  const handlePeriodChange = (newPeriod: string) => {
    setFilterPeriod(newPeriod);
    fetchFilteredData(filterYear, newPeriod, filterGenba, filterEmployeeName);
  };

  const handleGenbaChange = (newGenba: string) => {
    setFilterGenba(newGenba);
    fetchFilteredData(filterYear, monthOrPeriod, newGenba, filterEmployeeName);
  };

  const handleEmployeeNameChange = (newEmployeeName: string) => {
    setFilterEmployeeName(newEmployeeName);
    fetchFilteredData(filterYear, monthOrPeriod, filterGenba, newEmployeeName);
  };

  const handleClearFilters = () => {
    setFilterYear('');
    setFilterMonth('');
    setFilterPeriod('');
    setFilterGenba('');
    setFilterEmployeeName('');
    fetchFilteredData();
    loadFilterOptions();
  };

  const onDeleteYearData = () => handleDeleteYearData(filterYear);
  const onDeleteMonthData = () => handleDeleteMonthData(filterYear, filterMonth);
  const onDeletePeriodData = () => handleDeletePeriodData(filterYear, filterPeriod);
  const onDeleteSelection = () =>
    handleDeleteSelection(filterYear, monthOrPeriod, filterGenba, filterEmployeeName);

  const selectionEnabled = dataType === 'bonus'
    ? !!(filterYear && filterPeriod)
    : !!(filterYear && filterMonth);

  return (
    <div className="data-display-container" style={{ position: 'relative' }}>
      <LoadingAndError loading={loading} error={error || null} />

      <DataTypeToggle dataType={dataType} onToggle={handleDataTypeToggle} />

      <div className="controls-section">
        <div className="filter-operations">
          <div className="filter-group">
            <label htmlFor="year-filter">年:</label>
            <select 
              id="year-filter"
              value={filterYear} 
              onChange={(e) => handleYearChange(e.target.value)}
            >
              <option value="">すべて</option>
              {availableYears.map(year => (
                <option key={year} value={year}>{year}年</option>
              ))}
            </select>
          </div>

          {dataType === 'salary' ? (
            <div className="filter-group">
              <label htmlFor="month-filter">月:</label>
              <select 
                id="month-filter"
                value={filterMonth} 
                onChange={(e) => handleMonthChange(e.target.value)}
              >
                <option value="">すべて</option>
                {availableMonths.map(month => (
                  <option key={month} value={month}>{month}月</option>
                ))}
              </select>
            </div>
          ) : (
            <div className="filter-group">
              <label htmlFor="period-filter">期:</label>
              <select 
                id="period-filter"
                value={filterPeriod} 
                onChange={(e) => handlePeriodChange(e.target.value)}
              >
                <option value="">すべて</option>
                {availablePeriods.map(period => (
                  <option key={period} value={period}>{period}月</option>
                ))}
              </select>
            </div>
          )}

          <div className="filter-group">
            <label htmlFor="genba-filter">現場:</label>
            <select 
              id="genba-filter"
              value={filterGenba} 
              onChange={(e) => handleGenbaChange(e.target.value)}
            >
              <option value="">すべて</option>
              {availableGenbas.map(genba => (
                <option key={genba} value={genba}>{genba}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label htmlFor="employee-name-filter">従業員名:</label>
            <select
              id="employee-name-filter"
              value={filterEmployeeName}
              onChange={(e) => handleEmployeeNameChange(e.target.value)}
              className="employee-name-select"
            >
              <option value="">すべて</option>
              {availableEmployees.map(employeeName => (
                <option key={employeeName} value={employeeName}>{employeeName}</option>
              ))}
            </select>
          </div>

          <button 
            className="clear-filters"
            onClick={handleClearFilters}
          >
            フィルタークリア
          </button>
        </div>

        <div className="delete-operations">
          <div className="delete-buttons">
            <button
              type="button"
              className="delete-btn delete-year"
              onClick={() => {
                if (window.confirm('本当に削除してもよろしいですか？')) {
                  onDeleteYearData();
                }
              }}
              disabled={deleteLoading || !filterYear}
              title={!filterYear ? '年を選択してください' : ''}
            >
              {deleteLoading ? '削除中...' : filterYear ? `${filterYear}年のデータを削除` : '選択年のデータを削除'}
            </button>

            {dataType === 'salary' ? (
              <button
                type="button"
                className="delete-btn delete-month"
                onClick={() => {
                  if (window.confirm('本当に削除してもよろしいですか？')) {
                    onDeleteMonthData();
                  }
                }}
                disabled={deleteLoading || !filterYear || !filterMonth}
                title={!filterYear || !filterMonth ? '年と月を選択してください' : ''}
              >
                {deleteLoading ? '削除中...' : filterYear && filterMonth ? `${filterYear}年${filterMonth}月のデータを削除` : '選択年月のデータを削除'}
              </button>
            ) : (
              <button
                type="button"
                className="delete-btn delete-period"
                onClick={() => {
                  if (window.confirm('本当に削除してもよろしいですか？')) {
                    onDeletePeriodData();
                  }
                }}
                disabled={deleteLoading || !filterYear || !filterPeriod}
                title={!filterYear || !filterPeriod ? '年と期を選択してください' : ''}
              >
                {deleteLoading ? '削除中...' : filterYear && filterPeriod ? `${filterYear}年${filterPeriod}期のデータを削除` : '選択年期のデータを削除'}
              </button>
            )}

            <button
              type="button"
              className="delete-btn delete-selection"
              onClick={onDeleteSelection}
              disabled={deleteLoading || !selectionEnabled}
              title={!selectionEnabled ? (dataType === 'salary' ? '年と月を選択してください' : '年と期を選択してください') : ''}
            >
              {deleteLoading ? (
                '削除中...'
              ) : (
                <span className="delete-selection-label">
                  選択されている
                  <br />
                  データを削除
                </span>
              )}
            </button>
          </div>
        </div>
      </div>

      <DeleteMessages
        deleteError={deleteError}
        deleteSuccess={deleteSuccess}
        onClearMessages={clearDeleteMessages}
      />

      <ConstructionDataTable
        dataType={dataType}
        filteredData={data}
        deleteLoading={deleteLoading}
        onDeleteIndividualRecord={(genbaName, employeeName, year, monthOrPeriod) => 
          handleDeleteIndividualRecord(genbaName, employeeName, year, monthOrPeriod)
        }
      />
    </div>
  );
};
